import React, { useEffect, useState } from 'react';
import api from '../api';

export default function SettingsSection({ token, setError }) {
  const [selectedTab, setSelectedTab] = useState('trustedDevice');
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (selectedTab === 'trustedDevice') {
      loadTrustedDevices();
    }
  }, [selectedTab]);

  const authHeaders = { Authorization: `Bearer ${token}` };

  async function loadTrustedDevices() {
    setLoading(true);
    setActionError('');
    try {
      const resp = await api.get('/auth/devices', { headers: authHeaders });
      setDevices(resp.data.devices || []);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to load trusted devices');
      setError(err.response?.data?.error || 'Failed to load trusted devices');
    } finally {
      setLoading(false);
    }
  }

  const addNewDevice = async () => {
    setActionError('');
    try {
      const resp = await api.post('/auth/devices', { label: 'Admin trusted device', deviceIdentifier: 'admin_web' }, { headers: authHeaders });
      if (resp.data?.device) {
        const device = resp.data.device;
        window.alert(`Device registered. ID: ${device.id}\nSecret: ${device.device_secret}\nSave this secret now; it is shown once.`);
      }
      loadTrustedDevices();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not add device');
    }
  };

  const verifyDevice = async (id) => {
    const password = window.prompt('Enter admin account password to verify this device');
    if (!password) return;
    setActionError('');
    try {
      await api.post(`/auth/devices/${id}/verify`, { password }, { headers: authHeaders });
      loadTrustedDevices();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not verify device');
    }
  };

  const removeDevice = async (id) => {
    setActionError('');
    try {
      await api.delete(`/auth/devices/${id}`, { headers: authHeaders });
      loadTrustedDevices();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not remove device');
    }
  };

  return (
    <div className="flex h-full">
      <aside className="w-60 border-r bg-white p-4">
        <h3 className="text-lg font-semibold mb-4">Settings</h3>
        <button
          className={`w-full text-left px-3 py-2 rounded-lg mb-2 ${selectedTab === 'trustedDevice' ? 'bg-indigo-100 text-indigo-800' : 'text-gray-700 hover:bg-gray-100'}`}
          onClick={() => setSelectedTab('trustedDevice')}
        >
          Trusted Device
        </button>
      </aside>

      <main className="flex-1 p-6 bg-gray-50">
        <h2 className="text-2xl font-bold mb-4">Trusted Device</h2>
        <p className="text-sm text-gray-600 mb-4">Manage trusted device auto-login for the admin account.</p>

        {selectedTab === 'trustedDevice' && (
          <div>
            <div className="mb-4">
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                onClick={addNewDevice}
              >
                Register New Device
              </button>
            </div>
            {loading ? (
              <p>Loading...</p>
            ) : actionError ? (
              <p className="text-red-500">{actionError}</p>
            ) : devices.length === 0 ? (
              <p>No trusted devices registered.</p>
            ) : (
              <div className="space-y-3">
                {devices.map((device) => {
                  const isVerified = device.is_verified === true || device.is_verified === 't' || device.is_verified === 'true' || device.is_verified === 1 || device.is_verified === '1';
                  return (
                    <div key={device.id} className="bg-white rounded-lg border p-3 shadow-sm flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{device.label || 'Unnamed'}</div>
                        <div className="text-sm text-gray-600">Status: {isVerified ? 'Verified' : 'Pending'}</div>
                        <div className="text-xs text-gray-500">Last used: {device.last_used_at || 'Never'}</div>
                      </div>
                      <div className="flex gap-2">
                        {!isVerified && (
                          <button
                            className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                            onClick={() => verifyDevice(device.id)}
                          >
                            Verify
                          </button>
                        )}
                        <button
                          className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          onClick={() => removeDevice(device.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
